/**
 * The FIELD GUIDE — a cute bestiary of every tank, opened from the pause menu.
 *
 * Each card shows a LIVE, slowly-rotating 3D render of the real tank prefab on a
 * little disc of map-coloured ground. To stay within one GL context we run ONE
 * shared offscreen renderer and, each frame, draw each on-screen card's tank into
 * that card's 2D <canvas> (off-screen cards are skipped). The render loop self-stops
 * the moment the grid leaves the DOM (guide closed) and frees the GL context.
 *
 * Cards borrow the "liquid glass" look from the bskyrts project: a frosted panel
 * with an iridescent chroma rim, a colour-keyed header band, and a drifting shader
 * lattice. Accent/name colours are lifted toward readable luminance so dark tanks
 * (brown, black, sniper) don't vanish into the dark card.
 */

import * as THREE from "three";
import { buildTank, type loadTankGeometries, type BuiltTank } from "../tank/prefab";
import { TANK_TYPES, UNLOCK_LAYER } from "../config";

type Geo = Awaited<ReturnType<typeof loadTankGeometries>>;

const VIEW_W = 252, VIEW_H = 200; // offscreen render size (the card canvas matches)
const GOLD = "#ffd86b";

interface Entry { id: string; move: string; weapon: string; blurb: string; }

/** Curated bestiary; sorted by first-appearance floor at the bottom so it reads as a climb.
 *  Lightweight markup in `move`/`weapon`/`blurb`: *bold* (the mechanic), _italic_ (flavour),
 *  ~underline~ (the key "how to beat it" tip). Rendered by fmt(). */
const BESTIARY: Entry[] = [
  { id: "brown", move: "Stationary", weapon: "Slow single shot", blurb: "The humble turret — sits still and lobs *one slow shell* at a time. _Easy pickings_, but a crowd of them still adds up." },
  { id: "grey", move: "Roams slowly", weapon: "Single shot · *1 bounce*", blurb: "Wanders the maze and *banks shots off a wall*. _The basic mobile threat._" },
  { id: "teal", move: "Stationary", weapon: "Fast double · 1 bounce", blurb: "Bolted down but _trigger-happy_ — *rapid two-shell bursts* that ricochet once." },
  { id: "yellow", move: "Drives", weapon: "Bouncing shot + *mines*", blurb: "Cruises around *dropping mines* in your path while it fires. ~Watch where you step.~" },
  { id: "red", move: "Fast & aggressive", weapon: "Quick double · *leads you*", blurb: "Charges in fast and *leads its shots*. ~Don't let it close the gap.~" },
  { id: "green", move: "Stationary", weapon: "Precise double · *2 bounces*", blurb: "A dug-in marksman with *near-perfect aim* and two-bounce bank shots. ~Pop it before it lines you up.~" },
  { id: "purple", move: "Fast", weapon: "Double shot + *mines*", blurb: "A quick miner — _fast, accurate_, and *litters the floor with mines*." },
  { id: "white", move: "Drives — *INVISIBLE*", weapon: "Double shot + mines", blurb: "*Completely invisible* in play: only its _tread trail_ on the floor gives it away. ~Watch the tracks.~" },
  { id: "black", move: "Very fast", weapon: "*Triple shot* + mines · armored", blurb: "The _elite all-rounder_ — *blazing speed, heavy fire, mines*, and a hit of armor." },
  { id: "brute", move: "Drives", weapon: "Double shot · *heavy armor*", blurb: "A *damage sponge* — three shields on top of its hull. ~Bring extra firepower.~" },
  { id: "kamikaze", move: "*Sprints* at you", weapon: "Detonates on contact", blurb: "No turret — it just charges at you and *explodes on impact*. ~Sidestep or shoot it down.~" },
  { id: "sniper", move: "Stationary", weapon: "God-aim · *5-bounce bank*", blurb: "A _red sight-line_ marks its shot. Lands *perfect five-bounce bank shots*. ~Break line of sight, or kill it first.~" },
  { id: "rocket", move: "Stationary", weapon: "Fast straight rockets", blurb: "An emplacement firing *very fast, NON-bouncing rockets*. _No bank to read_ — ~just clear the line.~" },
  { id: "thrower", move: "Stays back", weapon: "*None* — throws allies", blurb: "*No attack of its own*; it _lobs chaser tanks_ right next to you. ~Annoying support — hunt it.~" },
  { id: "artillery", move: "Seeks high ground", weapon: "Arcing mortar · *ignores walls*", blurb: "Lobs shells *in an arc over every wall*. ~Watch the red landing ring fade in — clear it before it's solid.~" },
  { id: "stalker", move: "*Kites* you", weapon: "God-aim · *5-bounce bank*", blurb: "A _mobile sniper_. Keeps its distance while *holding a perfect bank shot*. ~Corner it.~" },
  { id: "spawner", move: "Flees", weapon: "*Summons tanks*", blurb: "_Harmless itself_, but flees while *conjuring a swarm*. ~Hunt it down first.~" },
  { id: "shieldgen", move: "Chases allies", weapon: "*None* — shields allies", blurb: "*Has no attack.* It races to nearby tanks and *wraps them in a shield*. ~Kill it to strip their protection.~" },
  { id: "tow", move: "*Kites* you", weapon: "Homing missiles", blurb: "Fires *slow heat-seeking missiles* that chase you around corners — _but_ ~you can shoot them out of the air.~" },
  { id: "marauder", move: "Drives", weapon: "*Very fast rockets*", blurb: "A mobile rocketeer hurling *streaking, non-bouncing rockets*. ~Keep moving.~" },
  { id: "laserbeam", move: "Stationary", weapon: "*Charged* bouncing beam", blurb: "Winds up a beam (_red → pink_) then fires a *bouncing laser* for three seconds. ~Leave the line before it goes live~ — shooting the beam staggers it." },
  { id: "gatling", move: "Slow & tanky", weapon: "*Rapid rocket stream*", blurb: "Hoses a *relentless cone of rapid rockets*. _Slow and heavily armored_ — ~flank it.~" },
  { id: "mlrs", move: "Stationary", weapon: "Rapid scatter salvos", blurb: "A bolted launcher that *rains a dozen wildly-scattered shells* at once, at *infinite range*. ~Keep moving through the rings.~" },
  { id: "abomination", move: "Drives", weapon: "*Everything* · huge HP", blurb: "A *rare boss* with a grab-bag of _every ability_ and a *mountain of health*. _Good luck._" },
  { id: "ally", move: "Fights for you", weapon: "Rapid double shots", blurb: "*Hire one in the round-end shop.* It _fights at your side_, dodges fire, and *follows you up the tower*." },
].sort((a, b) => (UNLOCK_LAYER[a.id] ?? 999) - (UNLOCK_LAYER[b.id] ?? 999));

/** Lift a colour toward readable luminance on a dark card while keeping its hue. */
function readable(hex: number): string {
  let r = (hex >> 16) & 255, g = (hex >> 8) & 255, b = hex & 255;
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  if (lum < 155) { const k = (155 - lum) / (255 - lum); r += (255 - r) * k; g += (255 - g) * k; b += (255 - b) * k; }
  return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
}

let stylesInjected = false;
export function injectStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;
  const s = document.createElement("style");
  s.textContent = `
.gb-card{position:relative;width:236px;border-radius:14px;overflow:hidden;isolation:isolate;
  background:linear-gradient(180deg,rgba(24,34,49,0.62) 0%,rgba(8,12,20,0.74) 100%);
  -webkit-backdrop-filter:blur(6px) saturate(1.25);backdrop-filter:blur(6px) saturate(1.25);
  border:1px solid rgba(120,150,190,0.28);
  box-shadow:inset 0 0 0 1px rgba(255,45,90,0.45),inset 0 0 0 2px rgba(255,170,40,0.32),
    inset 0 0 0 3px rgba(120,255,90,0.26),inset 0 0 0 4px rgba(45,200,255,0.22),
    inset 0 0 0 5px rgba(160,80,255,0.18),inset 0 0 11px 2px rgba(255,255,255,0.07),
    0 8px 22px rgba(0,0,0,0.5);
  animation:gbDrift 7s ease-in-out infinite;}
.gb-card::before{content:"";position:absolute;top:0;left:0;right:0;height:5px;z-index:1;
  background:var(--tc);box-shadow:0 0 12px var(--tc);opacity:0.95;}
.gb-card::after{content:"";position:absolute;inset:0;pointer-events:none;z-index:1;opacity:0.10;
  background-image:radial-gradient(circle,var(--tc) 1px,transparent 1.6px),
    repeating-linear-gradient(35deg,transparent 0 23px,var(--tc) 23px 24px);
  background-size:9px 9px,auto;animation:gbPat 7s linear infinite;}
.gb-card>*{position:relative;z-index:2;}
.gb-card canvas{display:block;width:100%;height:auto;}
@keyframes gbPat{to{background-position:-27px 18px,40px -24px;}}
@keyframes gbDrift{0%,100%{transform:translate(0,0)}25%{transform:translate(1px,-2px)}50%{transform:translate(-1px,1px)}75%{transform:translate(-1px,-1px)}}
.gb-card:nth-child(2n){animation-duration:8.1s;animation-delay:-2.3s}
.gb-card:nth-child(3n){animation-duration:6.3s;animation-delay:-3.9s}
@media (prefers-reduced-motion:reduce){.gb-card,.gb-card::after{animation:none}}
`;
  document.head.appendChild(s);
}

export class Guidebook {
  private renderer?: THREE.WebGLRenderer;
  private scene?: THREE.Scene;
  private cam?: THREE.PerspectiveCamera;
  private inScene?: THREE.Object3D; // the tank currently parented in the scene
  private ground?: THREE.Mesh; // the map-coloured platform disc (scaled per tank)
  private built = new Map<string, { tank: BuiltTank; radius: number; cy: number }>();
  private cards: { canvas: HTMLCanvasElement; id: string }[] = [];
  private scrollEl?: HTMLElement;
  private raf = 0;
  private gen = 0;   // bumped on each (re)open so a stale loop self-cancels
  private spin = 0;  // shared turntable angle

  constructor(private geo: Geo, private groundHex: number) {}

  // ---- offscreen studio --------------------------------------------------

  private ensureRenderer(): void {
    if (this.renderer) return;
    // preserveDrawingBuffer so drawImage(GL canvas → card 2D canvas) is reliable on every
    // browser/driver (some clear the back-buffer on the swap); the renderer is reused, so
    // the minor cost is paid once.
    const r = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    r.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    r.setSize(VIEW_W, VIEW_H);
    r.shadowMap.enabled = true;
    r.shadowMap.type = THREE.PCFSoftShadowMap;
    r.toneMapping = THREE.ACESFilmicToneMapping;
    r.toneMappingExposure = 1.25;
    this.renderer = r;

    const sc = new THREE.Scene();
    sc.add(new THREE.AmbientLight(0xffffff, 0.85));
    sc.add(new THREE.HemisphereLight(0xcfe4ff, 0x2a2620, 0.7));
    const sun = new THREE.DirectionalLight(0xfff2d6, 1.2);
    sun.position.set(70, 140, 90);
    sun.castShadow = true;
    sun.shadow.mapSize.set(512, 512);
    const c = sun.shadow.camera;
    c.left = -90; c.right = 90; c.top = 90; c.bottom = -90; c.near = 1; c.far = 600;
    sun.shadow.bias = -0.0008;
    sc.add(sun, sun.target);

    // a disc of map-coloured ground for the tank to stand on (+ catch its shadow)
    const ground = new THREE.Mesh(
      new THREE.CircleGeometry(95, 48),
      new THREE.MeshLambertMaterial({ color: this.groundHex }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.2;
    ground.receiveShadow = true;
    sc.add(ground);
    this.ground = ground; // base radius 95; scaled per tank so it reads as a platform
    this.scene = sc;

    this.cam = new THREE.PerspectiveCamera(28, VIEW_W / VIEW_H, 1, 4000);
  }

  /** Build (once) + measure a tank; ghosts show their model here so players know the shape. */
  private builtFor(id: string): { tank: BuiltTank; radius: number; cy: number } {
    const hit = this.built.get(id);
    if (hit) return hit;
    const type = TANK_TYPES[id];
    const tank = buildTank(this.geo, type.color, false, id === "ally" ? undefined : id, UNLOCK_LAYER[id] ?? 0);
    tank.root.traverse((o) => { if (o instanceof THREE.Mesh) o.castShadow = true; });
    const box = new THREE.Box3().setFromObject(tank.root);
    const size = box.getSize(new THREE.Vector3());
    const radius = Math.max(Math.hypot(size.x, size.z) * 0.5, size.y * 0.5, 24); // fits at any spin
    const cy = (box.min.y + box.max.y) / 2;
    const entry = { tank, radius, cy };
    this.built.set(id, entry);
    return entry;
  }

  private renderTank(id: string, canvas: HTMLCanvasElement): void {
    const r = this.renderer!, sc = this.scene!, cam = this.cam!;
    const b = this.builtFor(id);
    if (this.inScene !== b.tank.root) {
      if (this.inScene) sc.remove(this.inScene);
      sc.add(b.tank.root);
      this.inScene = b.tank.root;
    }
    b.tank.root.rotation.y = this.spin;
    if (this.ground) this.ground.scale.setScalar((b.radius * 1.2) / 95); // a snug platform disc, not a full field
    const dist = (b.radius / Math.tan((cam.fov * Math.PI) / 180 / 2)) * 1.5; // extra margin so the glass card shows around the portrait
    cam.position.set(0, dist * 0.6 + b.cy, dist * 0.8); // ~37° elevation — reads as a tank on a disc
    cam.lookAt(0, b.cy * 0.6, 0);
    r.render(sc, cam);
    const ctx = canvas.getContext("2d");
    if (ctx) { ctx.clearRect(0, 0, canvas.width, canvas.height); ctx.drawImage(r.domElement, 0, 0, canvas.width, canvas.height); }
  }

  // ---- shared studio API (reused by the card-mode hand) ------------------

  /** Render tank `id`'s live portrait into `canvas` using the shared offscreen studio.
   *  The card-mode hand drives this directly (one card per frame) from the main loop,
   *  so the gorgeous field-guide portraits also appear on the deployable cards. */
  renderPortrait(id: string, canvas: HTMLCanvasElement): void {
    this.ensureRenderer();
    this.renderTank(id, canvas);
  }
  /** Advance the shared turntable angle (the hand loop calls this once per frame). */
  advanceSpin(d = 0.02): void { this.spin += d; }

  // ---- the loop ----------------------------------------------------------

  private start(): void {
    this.ensureRenderer();
    const myGen = ++this.gen;
    let last = 0;
    const tick = (t: number) => {
      if (myGen !== this.gen) return; // superseded by a newer open
      const wrap = this.scrollEl;
      if (!wrap || !wrap.isConnected) { this.stop(); return; } // guide closed → tear down
      this.raf = requestAnimationFrame(tick);
      if (last && t - last < 28) return; // ~33fps is plenty for a turntable (eases the GPU)
      last = t;
      this.spin += 0.02;
      const wr = wrap.getBoundingClientRect();
      for (const card of this.cards) {
        const cr = card.canvas.getBoundingClientRect();
        if (cr.bottom < wr.top - 30 || cr.top > wr.bottom + 30) continue; // off-screen — skip
        this.renderTank(card.id, card.canvas);
      }
    };
    this.raf = requestAnimationFrame(tick);
  }

  /** Stop the render loop (idempotent). The renderer/scene/cached tanks are kept ALIVE
   *  for the next open: disposing + recreating the GL context would orphan the cached
   *  tanks' materials (reused in a fresh context → broken renders). One persistent
   *  offscreen context is well within GPU limits, and it sits idle (no renders) when closed. */
  stop(): void {
    this.gen++; // invalidate any in-flight tick
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = 0; }
  }

  // ---- content -----------------------------------------------------------

  /** Build the card grid and start the live render loop. */
  buildContent(): HTMLElement {
    injectStyles();
    const grid = document.createElement("div");
    grid.style.cssText =
      "display:flex;flex-wrap:wrap;gap:13px;justify-content:center;align-content:flex-start;" +
      "max-height:64vh;overflow-y:auto;overflow-x:hidden;padding:6px;text-align:left";
    this.cards = [];

    for (const e of BESTIARY) {
      const type = TANK_TYPES[e.id];
      if (!type) continue;
      const accent = readable(type.color);
      const unlock = UNLOCK_LAYER[e.id];
      const floorChip = e.id === "ally"
        ? `<span style="font-size:11px;color:#46e6b4;border:1px solid #2c5d52;border-radius:10px;padding:1px 8px">hire in shop</span>`
        : `<span style="font-size:11px;color:${GOLD};border:1px solid #5a4a1e;border-radius:10px;padding:1px 8px">floor ${(unlock ?? 0) + 1}+</span>`;
      const hp = type.health > 1
        ? `<span style="margin-left:auto;font-size:10.5px;color:#7fd0ff;border:1px solid #2c4a5d;border-radius:10px;padding:1px 7px;white-space:nowrap">${type.health} hits</span>` : "";

      const card = document.createElement("div");
      card.className = "gb-card";
      card.style.setProperty("--tc", accent);

      const canvas = document.createElement("canvas");
      canvas.width = VIEW_W; canvas.height = VIEW_H;
      card.appendChild(canvas);

      const info = document.createElement("div");
      info.style.cssText = "padding:8px 11px 11px;display:flex;flex-direction:column;gap:5px";
      info.innerHTML =
        `<div style="display:flex;align-items:center;justify-content:space-between;gap:6px">` +
        `<span style="font-weight:900;font-size:22px;line-height:1.05;color:${accent};letter-spacing:0.8px;text-shadow:0 2px 5px #000,0 0 14px rgba(0,0,0,0.6)">${esc(type.name)}</span>${floorChip}</div>` +
        `<div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center;font-size:11.5px;color:#aab4c2">` +
        `<span style="color:#d3dae3">${fmt(e.move)}</span><span style="color:#566273">·</span><span>${fmt(e.weapon)}</span>${hp}</div>` +
        `<div style="font-size:12px;line-height:1.5;color:#d2d8e2">${fmt(e.blurb)}</div>`;
      card.appendChild(info);

      grid.appendChild(card);
      this.cards.push({ canvas, id: e.id });
    }

    this.scrollEl = grid;
    this.start();
    return grid;
  }
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));
}

/** Escape, then apply the bestiary's lightweight markup: *bold* / _italic_ / ~underline~.
 *  Markers can't collide (distinct chars) and are converted AFTER escaping, so the only
 *  tags in the output are the ones we add. Bold is brightened so it really pops. */
function fmt(s: string): string {
  return esc(s)
    .replace(/\*([^*]+)\*/g, '<b style="color:#fff;font-weight:800">$1</b>')
    .replace(/~([^~]+)~/g, '<u style="text-decoration-color:#7fd0ff;text-underline-offset:2px">$1</u>')
    .replace(/_([^_]+)_/g, "<i>$1</i>");
}
